import { BatonLogo } from "@baton/brand";
import { useI18n } from "@baton/i18n";
import type { ReactNode } from "react";
import { cn } from "../cn.ts";
import { SkeletonBlock, SkeletonRegion } from "../feedback/Skeleton/Skeleton.tsx";
import { frameGroundClassName, frameSheetClassName } from "../primitives/layout/Frame/Frame.ts";

/**
 * § Authentication and onboarding, LoginCard: the frame every authentication screen renders
 * into, a card that is a small copy of the app's shell (2026-09-27, by the owner's decision: "how
 * it was before, but with the sidebar theme as part of the card, from the bottom"), in the app's own
 * three layers in both themes (the owner, 2026-09-27: "I can see how our sidebar and rail have two
 * different colours. We want that same mix"): the card is the sidebar's chrome ground (`.b-chrome`,
 * `--color-chrome-surface`) and the sheet is the content view's. The page is `--color-bg-backdrop`,
 * the owner's `#2a2a2a` in both themes (2026-09-28: the rail's canvas was "way too dark"), lighter
 * than the card, so the card reads as the darker object with no edge of its own. The card is
 * `--login-width` wide at `--login-radius`, the work sheet's radius plus its inset so the corners
 * run concentric. Inside it the app's work sheet is inset
 * `--surface-inset` on every side, as the content view sits in the chrome, and follows the theme as
 * that sheet does; it holds the product's logo, the heading, the description when the screen has
 * one and the methods. Under the sheet the chrome shows as the card's foot, part of the same dark
 * container, and holds the screen's secondary line in the chrome's inks, monochrome like the rest of
 * the chrome: the line in the resting ink and its link in the strong ink, semibold, underlined on hover
 * and on focus. The line is the route links that leave the screen, or the line that says sign-up is by
 * invitation. A screen with no secondary line shows
 * the chrome as an even edge all round. The ground and the sheet are the shell's own pieces
 * (`primitives/layout/Frame`), not copies of them, and the logo is `@baton/brand`'s. The screen's name for the instance, when
 * it has one, is part of its title ("Sign in to Acme"), as Linear, Vercel and Clerk title theirs.
 * `loading` renders the skeleton of the same layout (FR-015-090) under the logo, which needs no data
 * and so paints at once.
 *
 * Taken from Spectrum UI's Login Card (`@spectrumui/login-card`, Apache-2.0) and themed through the
 * variables of `packages/tokens` (2026-09-18, slice (f)): the centred card, the head, the methods,
 * the "or" rule whose hairlines fade out at both ends, and the source's entrance, the card fading
 * in while its head and its body rise into place one after the other. The entrance is written with
 * the `animate-in` utilities and the duration tokens rather than with Motion (2026-09-21, T292): the
 * public entry mounts this card and the engine it imported cost the public chunk 41 KB gzipped
 * against a 135 KB budget. Reduced motion, from the system or from `data-motion="reduced"`,
 * resolves those tokens to zero and shows the card at rest.
 *
 * What differs from the source, and why:
 * - the card is two surfaces and the source's is one: the shell's chrome around the shell's sheet,
 *   so the sign-in screens read as the product they open onto;
 * - the card takes the concentric `--login-radius` and no edge or shadow, where the source writes a
 *   2xl shadow and a backdrop blur, because the card is darker than the page it sits on, which
 *   parts the two as the app's tone steps part its rail, sidebar and sheet;
 * - the heading is the type scale's h2 step, the dialog title's, where the source sets a light 30 px
 *   title, because the card is dialog-sized and the scale has no light weight; it is still the
 *   page's `h1` and takes focus on navigation (FR-015-007);
 * - the entrance runs on the slow duration token, where the source takes 600 ms and 800 ms, because
 *   the token tiers are the vocabulary every other surface animates in and the longest is 250 ms;
 * - the source's pill input, its arrow button and the hover scale of its provider buttons are not
 *   carried: the methods are the host's, composed from the slice (a) Input and Button, which have a
 *   visible label and the control radius (FR-015-100), and the provider buttons carry their brand
 *   logos (`@baton/icons` brands);
 * - the sign-up line inside the source's card sits in the chrome under the sheet, while a link that
 *   belongs to one field, such as Forgot password under the password, stays in the sheet under that
 *   field (2026-09-27);
 * - the skeleton of the same layout stands in while `GET /instance` resolves, and does not animate
 *   in, so a slow instance read does not replay the entrance.
 */
export interface LoginCardProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly loading?: boolean;
  readonly footer?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
  /**
   * The wide card of a multi-step form: the onboarding wizard and Create workspace draw into it at
   * the dialog's medium width, so the sign-in screens, the first-run setup and the onboarding are
   * one layout (2026-09-28, the coordinator's check of the owner's "shouldn't this be centered
   * also?").
   */
  readonly wide?: boolean;
  /** The heading that receives focus on navigation (FR-015-007). */
  readonly headingRef?: React.Ref<HTMLHeadingElement>;
}

/**
 * The parts an authentication screen lays out in the sheet: a form, a link that belongs to the
 * field above it, the form's actions, a column of methods, a tinted panel, the invitation's terms,
 * a centred wait and a line of summary. The actions stack at the sheet's full width with the
 * primary action first, as every product studied sets its submit (2026-09-27).
 */
export const loginFormClassName = "flex flex-col gap-3";
export const loginFieldLinkClassName = "-mt-1 flex justify-end text-small";
export const loginActionsClassName = "mt-1 flex flex-col gap-2";
export const loginMethodsClassName = "flex flex-col gap-2";
export const loginPanelClassName =
  "flex flex-col gap-2 rounded-lg border border-border-subtle bg-bg-subtle p-3 data-[accent=amber]:border-accent-amber-solid data-[accent=amber]:bg-accent-amber-bg data-[accent=red]:border-accent-red-solid data-[accent=red]:bg-accent-red-bg";
export const loginInviteClassName = "grid grid-cols-[minmax(var(--space-9),auto)_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-lg border border-border-subtle bg-bg-subtle p-3 text-small [&_dd]:m-0 [&_dd]:min-w-0 [&_dd]:wrap-anywhere [&_dd]:font-(weight:--text-body-strong-weight) [&_dt]:text-text-tertiary";
export const loginCentredClassName = "flex flex-col items-center gap-3 py-6 text-center";
export const loginSummaryClassName = "m-0 text-work-text-tertiary";

/** The entrance: the card fades in, and the sheet's two parts rise into it one after the other. */
const cardEnterClassName = "animate-in fade-in-0 duration-(--duration-slow) ease-(--ease-out) motion-reduce:animate-none";
const partEnterClassName = "animate-in fade-in-0 slide-in-from-bottom-4 duration-(--duration-slow) ease-(--ease-out) fill-mode-backwards motion-reduce:animate-none";

/** The card: the sidebar's chrome on the lighter backdrop, its corners the sheet's radius plus the sheet's inset so the two run concentric. */
const cardClassName = "box-border flex w-full max-w-(--login-width) flex-col rounded-(--login-radius) data-[wide=true]:max-w-(--dialog-width-md)";
/** The screen inside the sheet. */
const sheetBodyClassName = "box-border flex flex-col px-6 py-8 sm:px-8";

/**
 * The product's logo at `--brand-logo-height` in the sheet's ink (2026-09-28, by the owner's decision:
 * "use the logos everywhere in the platform"). It is the card's one brand text, since the heading
 * names the instance, so it carries the product's name.
 */
function Logo() {
  const { t } = useI18n();
  return <BatonLogo label={t("app.name")} className="mx-auto mb-4 block h-(--brand-logo-height) w-auto shrink-0 text-work-text" />;
}

export function LoginCard({ title, description, loading = false, footer, children, className, headingRef, wide = false }: LoginCardProps) {
  return (
    <div data-slot="login" className="box-border flex min-h-dvh flex-col items-center justify-center bg-bg-backdrop px-4 py-12 text-chrome-text">
      <div data-slot="login-frame" data-chrome data-wide={wide ? "true" : undefined} className={cn(frameGroundClassName, cardClassName, !loading && cardEnterClassName, className)}>
        <div data-slot="login-sheet" className={frameSheetClassName}>
          {loading ? (
            <main id="main" data-slot="login-card" className={sheetBodyClassName} aria-busy="true">
              <Logo />
              <SkeletonRegion className="flex flex-col items-center">
                <SkeletonBlock className="h-(--text-h2-line) w-3/5" />
                <span data-slot="login-skeleton-controls" className="mt-6 flex w-full flex-col gap-3">
                  <SkeletonBlock className="h-(--control-height-md)" />
                  <SkeletonBlock className="h-(--control-height-md)" />
                  <SkeletonBlock className="h-(--control-height-md)" />
                </span>
              </SkeletonRegion>
            </main>
          ) : (
            <main id="main" data-slot="login-card" className={sheetBodyClassName}>
              <div className={cn(partEnterClassName, "flex flex-col text-center")}>
                <Logo />
                <h1 ref={headingRef} tabIndex={-1} className="m-0 text-h2 text-balance outline-none">
                  {title}
                </h1>
                {description !== undefined && <p className="mt-1 mb-0 text-pretty text-work-text-tertiary">{description}</p>}
              </div>
              <div className={cn(partEnterClassName, "mt-6 flex flex-col gap-3 [animation-delay:var(--duration-fast)]")}>{children}</div>
            </main>
          )}
        </div>
        {footer !== undefined && !loading && (
          <footer data-slot="login-strip" className="px-6 pt-2 pb-4 text-center text-small text-chrome-text [--color-focus-ring:var(--focus-dark)] [&_[data-slot=link]]:text-chrome-text-strong [&_[data-slot=link]:focus-visible]:underline">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/** The "or" rule between two methods in the sheet, the source's: a hairline on each side of the word that fades out at both ends. */
export function MethodSeparator({ label }: { readonly label: string }) {
  return (
    <div data-slot="method-separator" className="flex items-center" role="separator" aria-label={label}>
      <span className="h-(--border-width) flex-1 bg-linear-to-r from-transparent via-border-strong to-transparent" aria-hidden="true" />
      <span className="px-3 text-caption text-text-tertiary uppercase" aria-hidden="true">
        {label}
      </span>
      <span className="h-(--border-width) flex-1 bg-linear-to-r from-transparent via-border-strong to-transparent" aria-hidden="true" />
    </div>
  );
}
